import { useCallback, useContext, useMemo } from "react";
import { useSelector } from "react-redux";
import { ICommandBarItemProps } from "@fluentui/react/lib/CommandBar";

import { TranslationContext } from "Components/Generic/Translator";
import { IToolbarItem } from "Model/Configuration/IToolbarItem";
import { selectIsToolbarIconsOnly } from "Store/Selectors/UI";
import { EntityType } from "Model/EntityType";
import { isTrue } from "Utils/Boolean";
import { selectSelectedOrActiveProjects } from "Store/Selectors/Projects";
import { useClone } from "./Hooks/UseClone";
import { useDelete } from "./Hooks/UseDelete";
import { useDuplicateCheck } from "./Hooks/UseDuplicateCheck";
import { useEdit } from "./Hooks/UseEdit";
import { useExport } from "./Hooks/UseExport";
import { useManageIgnoredSourceData } from "./Hooks/UseManageIgnoredSourceData";
import { useRefresh } from "./Hooks/UseRefresh";
import { useRelevance } from "./Hooks/UseRelevance";
import { useStaging } from "./Hooks/UseStaging";
import { useImport } from "./Hooks/UseImport";
import { useAddressValidation } from "./Hooks/UseAddressValidation";
import { IAction } from "./Hooks/IAction";
import { useCopyProjectLink } from "./Hooks/UseCopyProjectLink";
import { useAssignMe } from "./Hooks/UseAssignMe";

export function useToolbar(type: EntityType): (items: IToolbarItem[]) => ICommandBarItemProps[] {
  const translator = useContext(TranslationContext);
  const iconOnly = useSelector(selectIsToolbarIconsOnly);
  const selectedProjects = useSelector(selectSelectedOrActiveProjects);
  const hasExportedProjects = selectedProjects.some((project) => isTrue(project?.[`@isExported`], false));

  const addressValidation = useAddressValidation();
  const clone = useClone();
  const copyProjectLink = useCopyProjectLink();
  const del = useDelete();
  const duplicateCheck = useDuplicateCheck();
  const edit = useEdit();
  const exp = useExport();
  const imp = useImport();
  const refresh = useRefresh();
  const relevance = useRelevance();
  const staging = useStaging();
  const stagingIgnore = useManageIgnoredSourceData();
  const assignToMe = useAssignMe();

  const hooks = useMemo<Record<string, [boolean, IAction]>>(
    () => ({
      av: addressValidation,
      clone,
      copyProjectLink,
      dc: duplicateCheck,
      delete: del,
      edit,
      export: exp,
      import: imp,
      refresh,
      relevance,
      staging,
      "staging.ignored": stagingIgnore,
      assignToMe
    }),
    [
      addressValidation,
      clone,
      copyProjectLink,
      duplicateCheck,
      del,
      edit,
      exp,
      imp,
      refresh,
      relevance,
      staging,
      stagingIgnore,
      assignToMe
    ]
  );

  const generate = useCallback(
    (item: IToolbarItem): ICommandBarItemProps => {
      const {
        key, defaultText, iconName, disableAfterExport
      } = item;

      const [allowAction, onActionClick] = hooks[key] ?? [null, null];

      if (onActionClick == null) {
        return null;
      }

      if ((type === EntityType.Company || type === EntityType.Contact) && key !== `edit`) {
        return null;
      }

      if (type === EntityType.Project && [`refresh`, `import`].includes(key)) {
        return null;
      }

      return {
        key,
        text: translator.get(`toolbar.${key}`, defaultText),
        iconProps: {
          iconName,
          className: `toolbar-${key}-icon`
        },
        onClick: onActionClick,
        iconOnly,
        disabled: shouldDisable(hasExportedProjects, disableAfterExport, allowAction)
      };
    },
    [hasExportedProjects, hooks, iconOnly, translator, type]
  );

  return useCallback((items: IToolbarItem[]) => items.map((i) => generate(i)).filter((i) => i != null), [generate]);
}

function shouldDisable(hasExportedProjects: boolean, disableAfterExport: boolean, allowAction: boolean): boolean {
  if (hasExportedProjects && disableAfterExport) {
    return true;
  }

  if (!allowAction) {
    return true;
  }

  return false;
}
